import http from '@/services/http';

/**
 * Vuex module for the physical-caja catalog (#363, `cat_cajas` on the tenant DB).
 * Shape mirrors `store/modules/customers.js` (bisstrack-front-conventions gold reference).
 *
 * `asignarTerminal` MUST NOT interpret `requiere_confirmacion` — the backend answers with a
 * plain 200 both on success AND when it needs a two-phase confirmation (design.md, "Códigos de
 * estado de PUT .../terminal"). Deciding what to do with that flag belongs to the component
 * (`DialogAsignarTerminal.vue`, WU7), not to this store — dialog state is not the store's job.
 */

const BASE = 'cajas-catalogo';

const state = () => ({
  cajasActivas: [],
  cajas: [],
  terminales: [],
  mpAlcanzable: null,
  loading: false,
  error: null,
  // #372 — quitar un posnet de una caja (DELETE .../:id_caja/terminal/:mp_terminal_id). Estado
  // PROPIO, separado del de asignarTerminal: quitar un posnet de una fila no debe pisar el
  // loading/error de la asignación de otra.
  quitarTerminalLoading: false,
  quitarTerminalError: null,
  // #372 — cuenta de empresa dueña del posnet. Estado PROPIO: fetchTerminalesConCuenta/
  // asignarTerminalACuenta/quitarTerminalDeCuenta no deben pisar loading/error de
  // fetchCajas/fetchTerminales/asignarTerminal de arriba (dominio caja<->terminal, #363).
  terminalesConCuenta: [],
  mpAlcanzableCuenta: null,
  terminalesConCuentaLoading: false,
  terminalesConCuentaError: null,
  asignarCuentaLoading: false,
  asignarCuentaError: null,
  quitarCuentaLoading: false,
  quitarCuentaError: null,
  // #372 tramo 3 — cobro real con Mercado Pago Point. Estado PROPIO: crearCobroPosnet/
  // consultarCobroPosnet no deben pisar loading/error de las acciones de arriba.
  cobroPosnetLoading: false,
  cobroPosnetError: null,
  consultarCobroError: null,
  // #538 — asistente "Activar modo integrado" (PATCH del modo PDV de un posnet). Estado PROPIO.
  activarModoLoading: false,
  activarModoError: null,
});

const getters = {
  activas: (state) => state.cajasActivas,
  all: (state) => state.cajas,
  isLoading: (state) => state.loading,
  hasError: (state) => state.error,
  quitarTerminalLoading: (state) => state.quitarTerminalLoading,
  quitarTerminalError: (state) => state.quitarTerminalError,
  // #372
  terminalesConCuenta: (state) => state.terminalesConCuenta,
  mpAlcanzableCuenta: (state) => state.mpAlcanzableCuenta,
  terminalesConCuentaLoading: (state) => state.terminalesConCuentaLoading,
  terminalesConCuentaError: (state) => state.terminalesConCuentaError,
  asignarCuentaLoading: (state) => state.asignarCuentaLoading,
  asignarCuentaError: (state) => state.asignarCuentaError,
  quitarCuentaLoading: (state) => state.quitarCuentaLoading,
  quitarCuentaError: (state) => state.quitarCuentaError,
  // #372
  cobroPosnetLoading: (state) => state.cobroPosnetLoading,
  cobroPosnetError: (state) => state.cobroPosnetError,
  consultarCobroError: (state) => state.consultarCobroError,
  // #538
  activarModoLoading: (state) => state.activarModoLoading,
  activarModoError: (state) => state.activarModoError,
};

const mutations = {
  SET_CAJAS_ACTIVAS(state, value) {
    state.cajasActivas = value;
  },
  SET_CAJAS(state, value) {
    state.cajas = value;
  },
  SET_TERMINALES(state, value) {
    state.terminales = value;
  },
  SET_MP_ALCANZABLE(state, value) {
    state.mpAlcanzable = value;
  },
  SET_LOADING(state, value) {
    state.loading = value;
  },
  SET_ERROR(state, value) {
    state.error = value;
  },
  SET_QUITAR_TERMINAL_LOADING(state, value) {
    state.quitarTerminalLoading = value;
  },
  SET_QUITAR_TERMINAL_ERROR(state, value) {
    state.quitarTerminalError = value;
  },
  // #372
  SET_TERMINALES_CON_CUENTA(state, value) {
    state.terminalesConCuenta = value;
  },
  SET_MP_ALCANZABLE_CUENTA(state, value) {
    state.mpAlcanzableCuenta = value;
  },
  SET_TERMINALES_CON_CUENTA_LOADING(state, value) {
    state.terminalesConCuentaLoading = value;
  },
  SET_TERMINALES_CON_CUENTA_ERROR(state, value) {
    state.terminalesConCuentaError = value;
  },
  SET_ASIGNAR_CUENTA_LOADING(state, value) {
    state.asignarCuentaLoading = value;
  },
  SET_ASIGNAR_CUENTA_ERROR(state, value) {
    state.asignarCuentaError = value;
  },
  SET_QUITAR_CUENTA_LOADING(state, value) {
    state.quitarCuentaLoading = value;
  },
  SET_QUITAR_CUENTA_ERROR(state, value) {
    state.quitarCuentaError = value;
  },
  // #372
  SET_COBRO_POSNET_LOADING(state, value) {
    state.cobroPosnetLoading = value;
  },
  SET_COBRO_POSNET_ERROR(state, value) {
    state.cobroPosnetError = value;
  },
  SET_CONSULTAR_COBRO_ERROR(state, value) {
    state.consultarCobroError = value;
  },
  // #538
  SET_ACTIVAR_MODO_LOADING(state, value) {
    state.activarModoLoading = value;
  },
  SET_ACTIVAR_MODO_ERROR(state, value) {
    state.activarModoError = value;
  },
};

const actions = {
  // GET /cajas-catalogo/activas — sólo DB (design.md D2). La consume el selector de apertura
  // del POS; nunca pega a Mercado Pago.
  async fetchActivas({ commit }) {
    try {
      commit('SET_LOADING', true);
      const { data } = await http.get(`${BASE}/activas`);
      const cajas = data.cajas || [];
      commit('SET_CAJAS_ACTIVAS', cajas);
      commit('SET_ERROR', null);
      return cajas;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Error al cargar las cajas');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // GET /cajas-catalogo — listado del ABM (roles [1,2]).
  async fetchCajas({ commit }) {
    try {
      commit('SET_LOADING', true);
      const { data } = await http.get(BASE);
      const cajas = data.cajas || [];
      commit('SET_CAJAS', cajas);
      commit('SET_ERROR', null);
      return cajas;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Error al cargar las cajas');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // GET /cajas-catalogo/terminales — pega a Mercado Pago (roles [1,2], sólo el ABM).
  async fetchTerminales({ commit }) {
    try {
      commit('SET_LOADING', true);
      const { data } = await http.get(`${BASE}/terminales`);
      commit('SET_TERMINALES', data.terminales || []);
      commit('SET_MP_ALCANZABLE', data.mp_alcanzable);
      commit('SET_ERROR', null);
      return data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Error al cargar los terminales');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async crearCaja({ commit }, { nombre }) {
    try {
      commit('SET_LOADING', true);
      const { data } = await http.post(BASE, { nombre });
      commit('SET_ERROR', null);
      return data.caja;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Error al crear la caja');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async editarCaja({ commit }, { id_caja, nombre }) {
    try {
      commit('SET_LOADING', true);
      const { data } = await http.put(`${BASE}/${id_caja}`, { nombre });
      commit('SET_ERROR', null);
      return data.caja;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Error al editar la caja');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Baja lógica (`activo=0`). El backend nunca hace DELETE real.
  async eliminarCaja({ commit }, id_caja) {
    try {
      commit('SET_LOADING', true);
      const { data } = await http.delete(`${BASE}/${id_caja}`);
      commit('SET_ERROR', null);
      return data.caja;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Error al dar de baja la caja');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // PUT /cajas-catalogo/:id_caja/terminal — AGREGA el posnet a la caja (#372: ya no reemplaza,
  // una caja acumula varios terminales de distintas cuentas). Devuelve la respuesta CRUDA. NO
  // interpreta `requiere_confirmacion`: eso es responsabilidad del componente (design.md).
  //
  // `confirmar_reemplazo_caja_abierta` YA NO EXISTE (#372): el backend borró el parámetro, su
  // validación y el guard que lo consumía — con la caja acumulando terminales, agregar uno nuevo
  // no le saca nada a la propia destino, así que no hay "reemplazo" que confirmar.
  async asignarTerminal({ commit }, { id_caja, mp_terminal_id, modo = null }) {
    try {
      commit('SET_LOADING', true);
      const { data } = await http.put(`${BASE}/${id_caja}/terminal`, {
        mp_terminal_id,
        modo,
      });
      commit('SET_ERROR', null);
      return data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Error al asignar el terminal');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // DELETE /cajas-catalogo/:id_caja/terminal/:mp_terminal_id — quita el vínculo (#372, NUEVO:
  // antes no existía forma de "soltar" un terminal sin reasignarlo a otra caja). Estado propio
  // (ver comentario en `state()`), separado del de asignarTerminal.
  async quitarTerminalDeCaja({ commit }, { id_caja, mp_terminal_id }) {
    try {
      commit('SET_QUITAR_TERMINAL_LOADING', true);
      const { data } = await http.delete(`${BASE}/${id_caja}/terminal/${mp_terminal_id}`);
      commit('SET_QUITAR_TERMINAL_ERROR', null);
      return data;
    } catch (error) {
      commit('SET_QUITAR_TERMINAL_ERROR', error.message || 'Error al quitar el terminal de la caja');
      throw error;
    } finally {
      commit('SET_QUITAR_TERMINAL_LOADING', false);
    }
  },

  // ─── #372 — cuenta de empresa dueña del posnet ───────────────────────────
  // Estado/loading/error PROPIOS (ver comentario en `state()`), a propósito separados de las
  // acciones de arriba (dominio caja física <-> terminal, #363).

  // GET /cajas-catalogo/terminales/con-cuenta — universo COMPLETO de terminales MP, cada uno
  // anotado con la cuenta que lo tiene (o null). `mp_alcanzable:false` no es un error: el
  // backend responde 200 igual (type:'warning'), así que esto NO hace catch() de eso; el
  // componente decide qué hacer con `mp_alcanzable` sin bloquear la pantalla.
  async fetchTerminalesConCuenta({ commit }) {
    try {
      commit('SET_TERMINALES_CON_CUENTA_LOADING', true);
      const { data } = await http.get(`${BASE}/terminales/con-cuenta`);
      commit('SET_TERMINALES_CON_CUENTA', data.terminales || []);
      commit('SET_MP_ALCANZABLE_CUENTA', data.mp_alcanzable);
      commit('SET_TERMINALES_CON_CUENTA_ERROR', null);
      return data;
    } catch (error) {
      commit('SET_TERMINALES_CON_CUENTA_ERROR', error.message || 'Error al cargar los terminales por cuenta');
      throw error;
    } finally {
      commit('SET_TERMINALES_CON_CUENTA_LOADING', false);
    }
  },

  // PUT /cajas-catalogo/terminales/:mp_terminal_id/cuenta — asigna o reasigna. Devuelve la
  // respuesta CRUDA (incluye `cuenta_anterior`, el NOMBRE de la cuenta de la que se sacó el
  // terminal en una reasignación) — el store no la interpreta, la vista es quien decide cómo
  // avisar "por nombre, nunca 'otra cuenta' a secas" (bisstrack-front-conventions).
  async asignarTerminalACuenta({ commit }, { mp_terminal_id, id_cuenta }) {
    try {
      commit('SET_ASIGNAR_CUENTA_LOADING', true);
      const { data } = await http.put(`${BASE}/terminales/${mp_terminal_id}/cuenta`, { id_cuenta });
      commit('SET_ASIGNAR_CUENTA_ERROR', null);
      return data;
    } catch (error) {
      commit('SET_ASIGNAR_CUENTA_ERROR', error.message || 'Error al asignar la cuenta del posnet');
      throw error;
    } finally {
      commit('SET_ASIGNAR_CUENTA_LOADING', false);
    }
  },

  // DELETE /cajas-catalogo/terminales/:mp_terminal_id/cuenta — desvincula el terminal de su cuenta.
  async quitarTerminalDeCuenta({ commit }, mp_terminal_id) {
    try {
      commit('SET_QUITAR_CUENTA_LOADING', true);
      const { data } = await http.delete(`${BASE}/terminales/${mp_terminal_id}/cuenta`);
      commit('SET_QUITAR_CUENTA_ERROR', null);
      return data;
    } catch (error) {
      commit('SET_QUITAR_CUENTA_ERROR', error.message || 'Error al quitar la cuenta del posnet');
      throw error;
    } finally {
      commit('SET_QUITAR_CUENTA_LOADING', false);
    }
  },

  // ─── #372 tramo 3 — cobro real con Mercado Pago Point ────────────────────
  // Reemplaza la simulación `Math.random()` que corría en el front: el "éxito" de un cobro
  // ahora SÓLO puede venir de acá. Estado/loading/error PROPIOS (ver comentario en `state()`).

  // POST /cajas-catalogo/cobros-posnet — devuelve la respuesta CRUDA: `sin_terminal`/
  // `requiere_seleccion` son 200 `type:"warning"`, no un error (spec del card #372) — el
  // componente decide qué hacer con cada forma, el store no las interpreta (mismo criterio que
  // `asignarTerminal`). Errores reales (400/403/404/409/503) SIEMPRE propagan tal cual: ninguna
  // falla se convierte acá en algo que parezca un cobro aprobado.
  async crearCobroPosnet({ commit }, {
    id_caja, id_cuenta, monto, mp_terminal_id = null, id_pedido = null, id_vendedor = null, id_medio_pago = null,
  }) {
    try {
      commit('SET_COBRO_POSNET_LOADING', true);
      const { data } = await http.post(`${BASE}/cobros-posnet`, {
        id_caja,
        id_cuenta,
        monto,
        mp_terminal_id,
        id_pedido,
        id_vendedor,
        // #372 tramo 4: medio elegido por el cajero — el backend lo mapea a
        // config.payment_method.default_type (PREFERENCIA, no restricción).
        id_medio_pago,
      });
      commit('SET_COBRO_POSNET_ERROR', null);
      return data;
    } catch (error) {
      commit('SET_COBRO_POSNET_ERROR', error.message || 'Error al iniciar el cobro con posnet');
      throw error;
    } finally {
      commit('SET_COBRO_POSNET_LOADING', false);
    }
  },

  // GET /cajas-catalogo/cobros-posnet/:external_reference — estado actual del cobro, para que
  // el POS lo polleé sin hablar con Mercado Pago directamente. Devuelve la respuesta CRUDA
  // (incluye `estado`, que puede ser no-terminal: el que polleá decide cuándo parar).
  async consultarCobroPosnet({ commit }, external_reference) {
    try {
      const { data } = await http.get(`${BASE}/cobros-posnet/${external_reference}`);
      commit('SET_CONSULTAR_COBRO_ERROR', null);
      return data;
    } catch (error) {
      commit('SET_CONSULTAR_COBRO_ERROR', error.message || 'Error al consultar el estado del cobro');
      throw error;
    }
  },

  // ─── #538 — asistente "Activar modo integrado" ───────────────────────────
  // Sólo PDV: no hay acción para STANDALONE ni SELF_SERVICE. `mp_terminal_id` es el id CRUDO de
  // Mercado Pago (`terminal.id`), nunca la etiqueta de `etiquetaPosnet`.

  // POST /cajas-catalogo/terminales/:mp_terminal_id/modo-pdv — sin body. Devuelve la respuesta
  // CRUDA (`ya_estaba_en_pdv`). Un 200 significa que Mercado Pago ACEPTÓ el cambio, no que el
  // equipo lo haya tomado: el componente lo verifica por polling y con confirmación humana.
  async activarModoPdv({ commit }, mp_terminal_id) {
    try {
      commit('SET_ACTIVAR_MODO_LOADING', true);
      const { data } = await http.post(`${BASE}/terminales/${encodeURIComponent(mp_terminal_id)}/modo-pdv`);
      commit('SET_ACTIVAR_MODO_ERROR', null);
      return data;
    } catch (error) {
      commit('SET_ACTIVAR_MODO_ERROR', error.message || 'Error al activar el modo integrado');
      throw error;
    } finally {
      commit('SET_ACTIVAR_MODO_LOADING', false);
    }
  },

  // POST /cajas-catalogo/terminales/:mp_terminal_id/modo-pdv/confirmacion — el usuario confirmó en el
  // equipo (paso 4): recién ahora el backend reconcilia `mp_estado` de las cajas. Ni la activación
  // ni el polling escriben nada; un asistente abandonado no deja el posnet marcado como listo.
  async confirmarModoPdv(context, mp_terminal_id) {
    const { data } = await http.post(`${BASE}/terminales/${encodeURIComponent(mp_terminal_id)}/modo-pdv/confirmacion`);
    return data;
  },

  // GET /cajas-catalogo/terminales/:mp_terminal_id/modo — `{ operating_mode, es_pdv }` para el
  // polling. Sin estado propio: lo consulta un loop y el que polleá decide qué hacer con un error.
  async consultarModoTerminal(context, mp_terminal_id) {
    const { data } = await http.get(`${BASE}/terminales/${encodeURIComponent(mp_terminal_id)}/modo`);
    return data;
  },

  // #537 — POST /cajas-catalogo/cobros-posnet/:external_reference/cancelar — el POS se rindió de
  // esperar: el backend lee la order REAL y devuelve qué pasó (`resultado`: cancelado |
  // en_dispositivo | processed | ya_finalizado | desconocido). Respuesta cruda; si falla, propaga
  // y el componente lo traduce a "no se pudo confirmar" (nunca a "cancelado").
  async cancelarCobroPosnet(_ctx, external_reference) {
    const { data } = await http.post(`${BASE}/cobros-posnet/${external_reference}/cancelar`, {});
    return data;
  },

  // #537 — GET /cajas-catalogo/cobros-posnet-disponibilidad?id_caja=&id_cuenta= — ¿hay un posnet
  // listo para esta caja física y esta cuenta? Sólo lectura: nunca crea un cobro. Sin estado de
  // loading/error propio a propósito: es una consulta de cortesía del POS (decide el modo inicial)
  // y NO debe pisar los flags de crearCobroPosnet. Un error propaga tal cual; el componente lo
  // traga y se queda en manual.
  async consultarDisponibilidadPosnet(_ctx, { id_caja, id_cuenta }) {
    const { data } = await http.get(`${BASE}/cobros-posnet-disponibilidad`, {
      params: { id_caja, id_cuenta },
    });
    return data;
  },

  // GET /cajas-catalogo/cobros-posnet/sin-registrar (#539) — cobros del posnet que el cliente pagó
  // pero que no quedaron en ninguna venta (el cajero siguió sin esperar, o la pantalla se cerró).
  // Respuesta CRUDA `{ cobros, total }`; solo lectura. Un error propaga: el componente oculta el
  // panel sin romper la vista.
  async listarCobrosPosnetSinRegistrar() {
    const { data } = await http.get(`${BASE}/cobros-posnet/sin-registrar`);
    return data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
